import { FC, useCallback, useEffect, useRef, useState } from 'react';
import agentsService from '@/services/agents.service';
import { AgentFile } from '@/types/agents';
import { useToast } from '@/contexts/ToastContext';
import { isQuotaError, quotaErrorMessage } from '@/utils/quotaErrorToast';
import AgentFileRow from '@/components/AgentFileRow';

const ACCEPT = '.pdf,.docx,.md,.txt';
const POLL_MS = 3000;
const isPending = (f: AgentFile) => f.status === 'UPLOADED' || f.status === 'PROCESSING';

interface Props {
  agentId: string | null;
}

const FilesTab: FC<Props> = ({ agentId }) => {
  const { showToast } = useToast();
  const [files, setFiles] = useState<AgentFile[]>([]);
  const [uploading, setUploading] = useState<{ filename: string; pct: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    if (!agentId) return;
    const fs = await agentsService.listFiles(agentId);
    setFiles(fs);
  }, [agentId]);

  useEffect(() => {
    if (agentId) void refresh();
  }, [agentId, refresh]);

  useEffect(() => {
    if (!agentId || !files.some(isPending)) return;
    const timer = setInterval(() => {
      refresh().catch(() => undefined);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [agentId, files, refresh]);

  if (!agentId) {
    return (
      <div data-testid="tab-files" className="rounded-lg border border-surface-border bg-surface-secondary/40 p-6 text-center text-content-tertiary">
        Salve o agente antes de subir arquivos.
      </div>
    );
  }

  const uploadAll = async (selected: File[]) => {
    for (const file of selected) {
      setUploading({ filename: file.name, pct: 0 });
      try {
        await agentsService.uploadFile(agentId, file, (pct) =>
          setUploading({ filename: file.name, pct }),
        );
      } catch (err) {
        if (isQuotaError(err)) showToast(quotaErrorMessage(err.response.data), 'error');
        else showToast(`Erro ao subir ${file.name}.`, 'error');
      }
    }
    setUploading(null);
    void refresh();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length) void uploadAll(dropped);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length) void uploadAll(selected);
    e.target.value = '';
  };

  const handleDelete = async (file: AgentFile) => {
    try {
      await agentsService.deleteFile(agentId, file.id);
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
    } catch {
      showToast('Não foi possível excluir o arquivo.', 'error');
    }
  };

  return (
    <div data-testid="tab-files" className="space-y-4">
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition ${
          dragging ? 'border-accent bg-accent/5' : 'border-surface-border bg-surface-secondary/30'
        }`}
      >
        <span className="material-symbols-outlined text-3xl text-content-tertiary">
          upload_file
        </span>
        <p className="mt-2 text-sm text-content-secondary">
          Arraste arquivos aqui ou{' '}
          <span className="text-accent">clique pra selecionar</span>
        </p>
        <p className="text-xs text-content-tertiary mt-1">PDF, DOCX, MD ou TXT</p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          data-testid="files-input"
          className="hidden"
          onChange={onInputChange}
        />
      </div>

      {uploading && (
        <div data-testid="files-upload-progress">
          <p className="text-xs text-content-secondary mb-1">
            Enviando {uploading.filename}… {uploading.pct}%
          </p>
          <div className="h-2 rounded bg-surface-tertiary overflow-hidden">
            <div className="h-full bg-accent transition-all" style={{ width: `${uploading.pct}%` }} />
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-content-primary mb-2">
          Arquivos ({files.length})
        </h3>
        {files.length > 0 ? (
          <ul className="space-y-2" data-testid="files-list">
            {files.map((f) => (
              <AgentFileRow key={f.id} file={f} onDelete={() => handleDelete(f)} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-content-tertiary">Nenhum arquivo ainda.</p>
        )}
      </div>
    </div>
  );
};

export default FilesTab;
